import React from 'react';
import {
    Mafs,
    Coordinates,
    Plot,
    Line,
    Circle,
    Vector,
    useStopwatch,
    Theme,
    Point
} from 'mafs';
import functionPlot from 'function-plot';
import mermaid from 'mermaid';
import { useTheme } from '../contexts/ThemeContext';

// Initialize functionPlot with default settings
if (typeof window !== 'undefined') {
    console.log('Initializing functionPlot');
    try {
        functionPlot({
            target: document.createElement('div'),
            width: 100,
            height: 100,
            data: [{ fn: 'x' }]
        });
        console.log('functionPlot initialized successfully');
    } catch (err) {
        console.error('Failed to initialize functionPlot:', err);
    }
}

// Helper function to parse and normalize equations
const normalizeEquation = (equation) => {
    // Remove spaces and convert to lowercase
    let normalized = equation.replace(/\s+/g, '').toLowerCase();
    
    // Replace common variations
    normalized = normalized
        .replace(/y=/i, '')  // Remove y=
        .replace(/f\(x\)/i, 'x')  // Replace f(x) with x
        .replace(/\^/g, '**');  // Convert ^ to **
        
    // Convert symbolic variables to x
    // This handles cases like P/V, m*x, etc. by converting them to numeric expressions
    normalized = normalized.replace(/[a-z]+/g, 'x');
    
    // Handle division
    normalized = normalized.replace(/(\d+)\/x/g, '$1/x');
    normalized = normalized.replace(/x\/x/g, '1');
    
    // Handle multiplication
    normalized = normalized.replace(/(\d+)x/g, '$1*x');
    
    // If the expression is just 'x', make it the identity function
    if (normalized === 'x') {
        normalized = '1*x';
    }
    
    console.log('Normalized equation:', normalized); // Debug log
    return normalized;
};

// Helper function to determine appropriate domain and range
const getPlotBounds = (equation, providedDomain, providedRange) => {
    // Use provided bounds if they exist and are valid
    const domain = Array.isArray(providedDomain) && providedDomain.length === 2 
        ? providedDomain 
        : [0.1, 10]; // Default domain that works well for most functions
    
    const range = Array.isArray(providedRange) && providedRange.length === 2
        ? providedRange
        : [0.1, 10]; // Default range that works well for most functions
    
    return { domain, range };
};

// Helper function to create a safe function from equation string
const createSafeFunction = (equation) => {
    try {
        // Handle specific known equations
        if (equation === 'V = V_i + Q / P') {
            return 'x';  // Linear function for constant pressure
        }
        if (equation === 'y = f(x)') {
            return 'x';  // Identity function as placeholder
        }
        
        // Remove everything before (and including) the first '=' if it exists
        let cleanEquation = equation.includes('=') 
            ? equation.split('=').slice(1).join('=').trim() 
            : equation.trim();

        // Convert caret to exponentiation operator and add Math. for functions
        cleanEquation = cleanEquation
            .replace(/\^/g, '**')
            .replace(/\b(?:sin|cos|tan|exp|log)\b/gi, 'Math.$&')
            // Replace any known symbols with default numeric values:
            .replace(/constant/gi, '1')
            .replace(/P/gi, '1')
            .replace(/V_i/gi, '1')
            .replace(/V/gi, 'x')
            .replace(/Q/gi, '1');

        // Test the cleaned-up function string
        const testFn = new Function('x', 'return ' + cleanEquation);
        const testVal = testFn(1);
        if (typeof testVal !== 'number' || isNaN(testVal)) {
            throw new Error('Function did not return a valid number');
        }
        console.log('Cleaned equation:', cleanEquation);
        return cleanEquation;
    } catch (e) {
        console.error('Error in createSafeFunction:', e);
        return 'x'; // Fallback
    }
};

const LectureVisualization = ({ type, data, description }) => {
    const { theme } = useTheme();
    const containerRef = React.useRef(null);
    const diagramRef = React.useRef(null);

    // Add error state
    const [error, setError] = React.useState(null);
    const [isLoading, setIsLoading] = React.useState(true);

    // Add data validation with detailed error messages
    const validateData = (type, data) => {
        try {
            if (!data) throw new Error('Missing visualization data');
            
            switch (type) {
                case 'function':
                    if (!data.equation) throw new Error('Missing equation in function visualization');
                    if (!data.domain) throw new Error('Missing domain in function visualization');
                    if (!data.range) throw new Error('Missing range in function visualization');
                    // Validate equation format
                    if (typeof data.equation !== 'string') throw new Error('Equation must be a string');
                    // Validate domain and range are arrays with two numbers
                    if (!Array.isArray(data.domain) || data.domain.length !== 2 || 
                        !data.domain.every(n => typeof n === 'number')) {
                        throw new Error('Domain must be an array of two numbers');
                    }
                    if (!Array.isArray(data.range) || data.range.length !== 2 || 
                        !data.range.every(n => typeof n === 'number')) {
                        throw new Error('Range must be an array of two numbers');
                    }
                    break;

                case 'geometric':
                    if (!data.type) throw new Error('Missing geometric type');
                    if (!data.properties) throw new Error('Missing geometric properties');
                    // Validate specific geometric types
                    switch (data.type) {
                        case 'circle':
                            if (!data.properties.center || !Array.isArray(data.properties.center) || 
                                data.properties.center.length !== 2) {
                                throw new Error('Circle requires center point as [x, y]');
                            }
                            if (typeof data.properties.radius !== 'number') {
                                throw new Error('Circle requires numeric radius');
                            }
                            break;
                        case 'vector':
                            if (!data.properties.tip || !Array.isArray(data.properties.tip) || 
                                data.properties.tip.length !== 2) {
                                throw new Error('Vector requires tip point as [x, y]');
                            }
                            break;
                        case 'rectangle':
                            if (!data.properties.layers || !Array.isArray(data.properties.layers)) {
                                throw new Error('Rectangle requires layers array');
                            }
                            break;
                    }
                    break;

                case 'diagram':
                    if (!data.nodes || !Array.isArray(data.nodes)) {
                        throw new Error('Diagram requires nodes array');
                    }
                    // Validate each node has required properties
                    data.nodes.forEach((node, index) => {
                        if (!node.id) throw new Error(`Node at index ${index} missing id`);
                        if (!node.label) throw new Error(`Node at index ${index} missing label`);
                    });
                    // Validate edges if present
                    if (data.edges) {
                        if (!Array.isArray(data.edges)) throw new Error('Edges must be an array');
                        data.edges.forEach((edge, index) => {
                            const hasSourceTarget = edge.source && edge.target;
                            const hasFromTo = edge.from && edge.to;
                            if (!hasSourceTarget && !hasFromTo) {
                                throw new Error(`Edge at index ${index} missing connection information`);
                            }
                        });
                    }
                    break;

                case 'statistical':
                    if (!data.type) throw new Error('Missing statistical visualization type');
                    if (!data.data || !Array.isArray(data.data)) {
                        throw new Error('Statistical visualization requires data array');
                    }
                    break;

                default:
                    throw new Error(`Unsupported visualization type: ${type}`);
            }
            return true;
        } catch (err) {
            setError(err.message);
            return false;
        }
    };

    React.useEffect(() => {
        let isMounted = true;

        const initializeVisualization = async () => {
            try {
                setIsLoading(true);
                setError(null);

                if (!validateData(type, data)) {
                    return;
                }

                if (type === 'function' && containerRef.current) {
                    try {
                        // Clear previous plot
                        containerRef.current.innerHTML = '';
                        
                        console.log('Original equation:', data.equation);
                        
                        // Get function and bounds
                        const fn = createSafeFunction(data.equation);
                        console.log('Processed function:', fn);
                        
                        const domain = data.domain || [-10, 10];
                        const range = data.range || [-10, 10];
                        
                        console.log('Plotting with config:', {
                            domain, range, fn
                        });

                        // Create plot configuration
                        const plotConfig = {
                            target: containerRef.current,
                            width: 600,
                            height: 400,
                            grid: true,
                            data: [{
                                fn,
                                graphType: 'polyline',
                                color: theme.accent
                            }],
                            xAxis: {
                                domain,
                                label: 'x'
                            },
                            yAxis: {
                                domain: range,
                                label: 'y'
                            }
                        };

                        // Try to plot
                        try {
                            functionPlot(plotConfig);
                            console.log('Plot successful');
                        } catch (plotError) {
                            console.error('Plot error:', plotError);
                            // Try fallback
                            functionPlot({
                                ...plotConfig,
                                data: [{
                                    fn: 'x',
                                    color: theme.accent
                                }]
                            });
                        }
                    } catch (err) {
                        console.error('Function plotting error:', err);
                        setError(`Failed to plot function: ${err.message}`);
                    }
                }

                if (type === 'diagram' && diagramRef.current && isMounted) {
                    try {
                        let definition = '';
                        
                        // Handle different diagram layouts
                        switch (data.layout) {
                            case 'network':
                                definition = 'graph LR;\n';
                                break;
                            case 'flowchart':
                                definition = 'flowchart TD;\n';
                                break;
                            default:
                                definition = 'graph TD;\n';
                        }
                        
                        // Add nodes with styling
                        data.nodes.forEach(node => {
                            definition += `${node.id}["${node.label}"];\n`;
                        });
                        
                        // Add edges
                        if (data.edges && data.edges.length > 0) {
                            data.edges.forEach(edge => {
                                const fromNode = edge.from || edge.source;
                                const toNode = edge.to || edge.target;
                                if (!fromNode || !toNode) {
                                    console.warn('Edge missing connection information:', edge);
                                    return;
                                }
                                definition += `${fromNode} --> ${toNode}${edge.label ? `|${edge.label}|` : ''};\n`;
                            });
                        }

                        // Clear previous diagram
                        diagramRef.current.innerHTML = '';
                        
                        // Create new diagram
                        const mermaidDiv = document.createElement('div');
                        mermaidDiv.className = 'mermaid';
                        mermaidDiv.textContent = definition;
                        
                        // Add theme-aware styling
                        const styleDiv = document.createElement('style');
                        styleDiv.textContent = `
                            .mermaid {
                                background: ${theme.secondary};
                                padding: 1rem;
                                border-radius: 0.5rem;
                            }
                            .mermaid .node rect, .mermaid .node circle, 
                            .mermaid .node ellipse, .mermaid .node polygon {
                                fill: ${theme.primary} !important;
                                stroke: ${theme.accent} !important;
                            }
                            .mermaid .node text {
                                fill: ${theme.text} !important;
                            }
                            .mermaid .edgePath .path {
                                stroke: ${theme.accent} !important;
                            }
                            .mermaid .arrowheadPath {
                                fill: ${theme.accent} !important;
                                stroke: ${theme.accent} !important;
                            }
                        `;
                        
                        if (isMounted) {
                            diagramRef.current.appendChild(styleDiv);
                            diagramRef.current.appendChild(mermaidDiv);
                            await mermaid.contentLoaded();
                        }
                    } catch (err) {
                        console.error('Error rendering diagram:', err);
                        setError(`Failed to render diagram: ${err.message}`);
                    }
                }
            } catch (err) {
                console.error('Visualization error:', err);
                if (isMounted) {
                    setError(`Failed to initialize visualization: ${err.message}`);
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        initializeVisualization();

        return () => {
            isMounted = false;
            // Remove any added styles
            const styles = document.querySelectorAll('style');
            styles.forEach(style => {
                if (style.textContent.includes('function-plot') || 
                    style.textContent.includes('mermaid')) {
                    style.remove();
                }
            });
        };
    }, [type, data, theme]);

    React.useEffect(() => {
        if (containerRef.current) {
            try {
                console.log('Testing functionPlot with simple equation');
                functionPlot({
                    target: containerRef.current,
                    width: 600,
                    height: 400,
                    grid: true,
                    data: [{
                        fn: 'x',
                        color: theme.accent
                    }]
                });
                console.log('Test plot successful');
            } catch (err) {
                console.error('Test plot failed:', err);
            }
        }
    }, []);

    const renderCellGrid = () => {
        const { width, height, layers } = data.properties;
        const gridSize = Math.max(width, height);
        const cellSize = 40;
        const totalSize = gridSize * cellSize;
        
        // Create a 2D array to represent the grid
        const grid = Array(height).fill().map(() => Array(width).fill(null));
        
        // Fill in the grid with values from layers
        layers.forEach(layer => {
            const value = layer.value || '';
            if (layer.position) {
                // Single position
                const [x, y] = layer.position;
                const adjustedX = x + Math.floor(width/2);
                const adjustedY = y + Math.floor(height/2);
                if (adjustedX >= 0 && adjustedX < width && adjustedY >= 0 && adjustedY < height) {
                    grid[adjustedY][adjustedX] = {
                        type: layer.type,
                        value: value
                    };
                }
            }
            if (layer.positions) {
                // Multiple positions
                layer.positions.forEach(([x, y]) => {
                    const adjustedX = x + Math.floor(width/2);
                    const adjustedY = y + Math.floor(height/2);
                    if (adjustedX >= 0 && adjustedX < width && adjustedY >= 0 && adjustedY < height) {
                        grid[adjustedY][adjustedX] = {
                            type: layer.type,
                            value: value
                        };
                    }
                });
            }
        });

        const getCellStyle = (cell) => {
            if (!cell) return { background: theme.background };
            
            switch (cell.type) {
                case 'cell_under_test':
                    return {
                        background: theme.primary,
                        color: theme.text,
                        border: `2px solid ${theme.accent}`
                    };
                case 'guard_cells':
                    return {
                        background: theme.secondary,
                        color: theme.text,
                        border: `1px solid ${theme.border}`
                    };
                case 'training_cells':
                    return {
                        background: theme.background,
                        color: theme.text,
                        border: `1px dashed ${theme.border}`
                    };
                default:
                    return {
                        background: theme.background,
                        color: theme.text
                    };
            }
        };

        return (
            <div className="p-4 border rounded" style={{ background: theme.secondary }}>
                <div 
                    className="grid gap-1" 
                    style={{ 
                        gridTemplateColumns: `repeat(${width}, ${cellSize}px)`,
                        width: 'fit-content',
                        margin: '0 auto'
                    }}
                >
                    {grid.map((row, i) => 
                        row.map((cell, j) => (
                            <div
                                key={`${i}-${j}`}
                                className="flex items-center justify-center text-sm font-mono"
                                style={{
                                    ...getCellStyle(cell),
                                    width: cellSize,
                                    height: cellSize,
                                    transition: 'all 0.2s'
                                }}
                            >
                                {cell?.value}
                            </div>
                        ))
                    )}
                </div>
                {description && (
                    <div className="mt-4 text-center text-sm" style={{ color: theme.accent }}>
                        {description}
                    </div>
                )}
            </div>
        );
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center p-4">
                <div className="animate-spin rounded-full h-8 w-8 border-2" style={{ 
                    borderColor: theme.accent,
                    borderTopColor: 'transparent'
                }}></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="my-6 p-4 rounded-lg" style={{ 
                background: theme.error, 
                color: '#FFFFFF',
                border: `1px solid ${theme.border}`
            }}>
                <p className="font-medium">Visualization Error</p>
                <p className="text-sm mt-1">{error}</p>
            </div>
        );
    }

    return (
        <div className="my-6">
            <div 
                className="mb-4 rounded-lg overflow-hidden" 
                style={{ 
                    background: theme.secondary,
                    border: `1px solid ${theme.border}`,
                    padding: '1rem',
                    width: '100%',
                    height: '400px'  // Explicit height
                }}
            >
                {type === 'function' && (
                    <div 
                        ref={containerRef} 
                        style={{ 
                            width: '100%',
                            height: '100%',
                            minHeight: '400px'
                        }}
                    />
                )}
                {type === 'geometric' && data.type === 'rectangle' && data.properties.layers && (
                    renderCellGrid()
                )}
                {type === 'geometric' && data.type !== 'rectangle' && (
                    <Mafs
                        width={600}
                        height={400}
                        viewBox={{ x: [-10, 10], y: [-10, 10] }}
                    >
                        <Coordinates />
                        {data.type === 'circle' && (
                            <Circle
                                center={data.properties.center || [0, 0]}
                                radius={data.properties.radius || 1}
                            />
                        )}
                        {data.type === 'vector' && (
                            <Vector
                                tip={data.properties.tip}
                                tail={data.properties.tail || [0, 0]}
                            />
                        )}
                    </Mafs>
                )}
                {type === 'diagram' && (
                    <div ref={diagramRef} className="w-full" />
                )}
                {type === 'statistical' && (
                    <div className="p-4 border rounded">
                        <p>Statistical visualization not yet implemented</p>
                    </div>
                )}
            </div>
            {description && (
                <p className="text-sm text-center" style={{ color: theme.accent }}>
                    {description}
                </p>
            )}
        </div>
    );
};

export default LectureVisualization; 